import { ArrowRightLeft, UserCheck, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { PersonalPartnerBalance } from '../../types';

interface PersonalPartnerDetailsWidgetProps {
  partnerBalance: PersonalPartnerBalance;
  partner1Name: string;
  partner2Name: string;
  formatCurrency: (amount: number) => string;
  isCurrentUserPartner1: boolean;
}

export default function PersonalPartnerDetailsWidget({
  partnerBalance,
  partner1Name,
  partner2Name,
  formatCurrency,
  isCurrentUserPartner1,
}: PersonalPartnerDetailsWidgetProps) {
  const { t } = useTranslation('dashboard');
  const userName = isCurrentUserPartner1 ? partner1Name : partner2Name;
  const partnerName = isCurrentUserPartner1 ? partner2Name : partner1Name;

  const {
    userSharedExpenses,
    userShareOfOwnExpenses,
    partnerShareOfUserExpenses,
    partnerSharedExpenses,
    partnerShareOfOwnExpenses,
    userShareOfPartnerExpenses,
    netBalance,
    isPartnerLinked,
  } = partnerBalance;

  const hasData = userSharedExpenses > 0 || partnerSharedExpenses > 0;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <ArrowRightLeft className="w-5 h-5 text-orange-600" />
        <h3 className="text-base font-semibold text-gray-900">{t('personalPartnerDetails.title')}</h3>
      </div>

      {!hasData ? (
        <p className="text-sm text-gray-500">
          {t('personalPartnerDetails.noSharedExpenses')}
        </p>
      ) : (
        <div className="space-y-4">
          {/* Résumé principal */}
          <div className={`flex items-center justify-between p-3 rounded-lg ${
            netBalance > 0
              ? 'bg-green-50 border border-green-200'
              : netBalance < 0
                ? 'bg-red-50 border border-red-200'
                : 'bg-gray-50 border border-gray-200'
          }`}>
            <span className="text-sm font-medium text-gray-700">
              {netBalance > 0
                ? t('personalPartnerDetails.owesYou', { partner: partnerName })
                : netBalance < 0
                  ? t('personalPartnerDetails.youOwe', { partner: partnerName })
                  : t('personalPartnerDetails.allSettled')}
            </span>
            {netBalance !== 0 && (
              <span className={`text-lg font-bold ${
                netBalance > 0 ? 'text-green-700' : 'text-red-700'
              }`}>
                {formatCurrency(Math.abs(netBalance))}
              </span>
            )}
          </div>

          {/* Détails utilisateur */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-gray-700">{userName} {t('personalPartnerDetails.you')}</span>
            </div>
            <div className="pl-6 space-y-1">
              <div className="flex justify-between text-xs text-gray-600">
                <span>{t('personalPartnerDetails.sharedExpensesPaid')}</span>
                <span className="font-medium">{formatCurrency(userSharedExpenses)}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <span>{t('personalPartnerDetails.yourShare')}</span>
                <span>{formatCurrency(userShareOfOwnExpenses)}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <span>{t('personalPartnerDetails.partnerShare', { partner: partnerName })}</span>
                <span className="font-medium text-green-700">{formatCurrency(partnerShareOfUserExpenses)}</span>
              </div>
            </div>
          </div>

          {/* Détails partenaire */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {isPartnerLinked ? (
                <UserCheck className="w-4 h-4 text-emerald-600" />
              ) : (
                <UserX className="w-4 h-4 text-gray-400" />
              )}
              <span className="text-sm font-medium text-gray-700">{partnerName}</span>
            </div>
            {isPartnerLinked ? (
              <div className="pl-6 space-y-1">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('personalPartnerDetails.sharedExpensesPaid')}</span>
                  <span className="font-medium">{formatCurrency(partnerSharedExpenses)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('personalPartnerDetails.theirShare')}</span>
                  <span>{formatCurrency(partnerShareOfOwnExpenses)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('personalPartnerDetails.yourShare')}</span>
                  <span className="font-medium text-red-700">{formatCurrency(userShareOfPartnerExpenses)}</span>
                </div>
              </div>
            ) : (
              <div className="pl-6">
                <p className="text-xs text-gray-500 italic">
                  {t('personalPartnerDetails.linkAccounts')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
